import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import API from "../../../../services/api";
import {
  Typography,
  Spin,
  Button,
  message,
  Empty,
  Tag,
  Space,
  Popconfirm,
  Pagination,
  Select,
  Modal,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  EnvironmentOutlined,
  CrownOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import "./MyFranchises.css";

const { Text, Paragraph } = Typography;

function MyFranchises() {
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [fetching, setFetching] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [stats, setStats] = useState({
    total: 0,
    activeCount: 0,
    pendingCount: 0,
    totalInvestment: 0,
  });
  const [isMobileFilter, setIsMobileFilter] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 460 : false
  );
  const navigate = useNavigate();

  const fetchMyFranchises = useCallback(
    async (pageNumber = 1) => {
      if (fetching) return;

      try {
        setFetching(true);
        setLoading(true);

        const res = await API.get(`/franchises/my/listings?page=${pageNumber}&filter=${filter}`);

        setFranchises(res.data.franchises || []);
        setTotalPages(res.data.totalPages || 1);
        setTotal(res.data.total || 0);
        setStats(res.data.stats || {});
      } catch (err) {
        console.error("Fetch error:", err);
        message.error("Failed to load listings");
        setFranchises([]);
      } finally {
        setLoading(false);
        setFetching(false);
      }
    }, [fetching, filter]);

  useEffect(() => {
    setPage(1);
    fetchMyFranchises(1);
  }, [filter]);

  useEffect(() => {
    fetchMyFranchises(page);
  }, [page]);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobileFilter(window.innerWidth <= 460);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleStatusToggle = async (id, currentStatus) => {
    try {
      setActionLoadingId(id);
      const endpoint = currentStatus === "active" ? "/deactivate" : "/activate";
      await API.put(`/franchises/${id}${endpoint}`);
      message.success(currentStatus === "active" ? "Deactivated" : "Activated");
      fetchMyFranchises(page);
    } catch (err) {
      message.error("Status update failed");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDelete = (id, title) => {
    Modal.confirm({
      title: "Delete Listing",
      content: `Are you sure you want to delete "${title}"? This action cannot be undone.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await API.delete(`/franchises/${id}`);
          message.success("Listing deleted successfully");
          setFranchises((prev) => prev.filter((f) => f._id !== id));
        } catch (err) {
          message.error("Failed to delete listing");
        }
      },
    });
  };

  const formatInvestment = (amount) => {
    if (!amount) return "Negotiable";
    return `₹${Number(amount).toLocaleString("en-IN")}`;
  };

  const getStatusConfig = (status) =>
    ({
      active: { color: "#10b981", bg: "#ecfdf5", label: "Active" },
      inactive: { color: "#6b7280", bg: "#f3f4f6", label: "Inactive" },
    })[status] || { color: "#ef4444", bg: "#fef2f2", label: "Error" };

  const getApprovalConfig = (status) =>
    ({
      approved: { color: "#3b82f6", bg: "#eff6ff", label: "Approved" },
      pending: { color: "#f59e0b", bg: "#fef3c7", label: "Pending" },
      rejected: { color: "#ef4444", bg: "#fef2f2", label: "Rejected" },
    })[status] || { color: "#6b7280", bg: "#f3f4f6", label: "Unknown" };

  if (loading) {
    return (
      <div className="frc-page">
        <div className="frc-shell">
          <div className="frc-loading">
            <Spin size="large" />
          </div>
        </div>
      </div>
    );
  }

  const activeCount = stats.activeCount;
  const pendingCount = stats.pendingCount;
  const totalInvestment = stats.totalInvestment || 0;

  const filteredFranchises = franchises;

  const filterOptions = [
    { label: "All", value: "all" },
    { label: "Approved", value: "approved" },
    { label: "Rejected", value: "rejected" },
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
  ];

  return (
    <div className="frc-page">
      <div className="frc-shell">
        {/* ── OVERVIEW ── */}
        <section className="frc-overview">
          <div className="frc-overview__stats">
            <div className="frc-overview__stat">
              <div className="frc-overview__value">{stats.total}</div>
              <div className="frc-overview__label">Total Listings</div>
            </div>

            <div className="frc-overview__stat">
              <div className="frc-overview__value frc-overview__value--active">
                {activeCount}
              </div>
              <div className="frc-overview__label">Active</div>
            </div>

            <div className="frc-overview__stat">
              <div className="frc-overview__value frc-overview__value--pending">
                {pendingCount}
              </div>
              <div className="frc-overview__label">Pending Review</div>
            </div>

            <div className="frc-overview__stat">
              <div className="frc-overview__value frc-overview__value--revenue">
                ₹{totalInvestment.toLocaleString()}
              </div>
              <div className="frc-overview__label">Est. Investment</div>
            </div>
          </div>

          <Link to="/add-franchise" className="frc-overview__cta">
            <Button size="large" type="primary" icon={<PlusOutlined />}>
              Add New Franchise
            </Button>
          </Link>
        </section>

        {/* ── TOOLBAR ── */}
        <section className="frc-toolbar">
          <div className="frc-toolbar__left">
            <div className="frc-toolbar__title-wrap">
              <Text className="frc-toolbar__title">My Franchises</Text>
              <Text className="frc-toolbar__subtitle">
                Manage all your franchise listings in one place
              </Text>
            </div>
          </div>

          <div className="frc-toolbar__right">
            {isMobileFilter ? (
              <Select
                value={filter}
                onChange={setFilter}
                options={filterOptions}
                className="frc-filters__select"
                classNames={{ popup: { root: "frc-filters__dropdown" } }}
                size="middle"
              />
            ) : (
              <div className="frc-filters">
                <Space wrap size={[10, 10]}>
                  <Button
                    type={filter === "all" ? "primary" : "default"}
                    onClick={() => setFilter("all")}
                  >
                    All
                  </Button>
                  <Button
                    type={filter === "approved" ? "primary" : "default"}
                    onClick={() => setFilter("approved")}
                  >
                    Approved
                  </Button>
                  <Button
                    type={filter === "rejected" ? "primary" : "default"}
                    onClick={() => setFilter("rejected")}
                  >
                    Rejected
                  </Button>
                  <Button
                    type={filter === "active" ? "primary" : "default"}
                    onClick={() => setFilter("active")}
                  >
                    Active
                  </Button>
                  <Button
                    type={filter === "inactive" ? "primary" : "default"}
                    onClick={() => setFilter("inactive")}
                  >
                    Inactive
                  </Button>
                </Space>
              </div>
            )}
          </div>
        </section>

        {/* ── EMPTY ── */}
        {franchises.length === 0 ? (
          <div className="frc-empty">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div>
                  <Text strong className="frc-empty__title">
                    No franchises yet
                  </Text>
                  <Paragraph className="frc-empty__text">
                    Create your first franchise listing to get started
                  </Paragraph>
                </div>
              }
            >
              <Link to="/add-franchise">
                <Button type="primary" size="large">
                  Create New Franchise
                </Button>
              </Link>
            </Empty>
          </div>
        ) : (
          <section className="frc-grid">
            {filteredFranchises.map((franchise) => (
              <article className="frc-card" key={franchise._id}>
                {/* ── Media ── */}
                <div className="frc-card__media">
                  <img
                    src={
                      franchise.thumbnail ||
                      franchise.images?.[0] ||
                      "/no-image.png"
                    }
                    alt={franchise.franchiseDetails?.brandName}
                    className="frc-card__image"
                  />

                  <div className="frc-card__badges">
                    <Tag
                      className="frc-card__badge"
                      style={{
                        background: getStatusConfig(franchise.status).bg,
                        color: getStatusConfig(franchise.status).color,
                        borderColor: "transparent",
                      }}
                    >
                      {getStatusConfig(franchise.status).label}
                    </Tag>
                    <Tag
                      className="frc-card__badge"
                      style={{
                        background: getApprovalConfig(franchise.approvalStatus)
                          .bg,
                        color: getApprovalConfig(franchise.approvalStatus)
                          .color,
                        borderColor: "transparent",
                      }}
                    >
                      {getApprovalConfig(franchise.approvalStatus).label}
                    </Tag>
                    {franchise.isFeatured && (
                      <Tag
                        className="frc-card__badge frc-card__badge--featured"
                        style={{
                          background: "#fef3c7",
                          color: "#d97706",
                          borderColor: "transparent",
                        }}
                      >
                        Featured
                      </Tag>
                    )}
                    <Button
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDelete(franchise._id, franchise.franchiseDetails?.brandName)}
                      className="frc-card__delete-btn"
                      danger
                    />
                  </div>
                </div>

                {/* ── Body ── */}
                <div className="frc-card__body">
                  <Text
                    className="frc-card__title"
                    title={franchise.franchiseDetails?.brandName}
                  >
                    {franchise.franchiseDetails?.brandName}
                  </Text>

                  <div className="frc-card__price">
                    <Text className="frc-card__price-value">
                      {formatInvestment(
                        franchise.franchiseDetails?.investmentRequired
                      )}
                    </Text>
                  </div>

                  <div className="frc-card__meta">
                    <div className="frc-card__meta-item">
                      <EnvironmentOutlined className="frc-card__meta-icon" />
                      <span>{franchise.headquarters?.city || "N/A"}</span>
                    </div>

                    <div className="frc-card__meta-item">
                      <CrownOutlined className="frc-card__meta-icon" />
                      <span>
                        {franchise.franchiseDetails?.category || "N/A"}
                      </span>
                    </div>
                  </div>

                  {/* ── Actions ── */}
                  <div className="frc-card__footer">
                    <div className="frc-card__actions">
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() =>
                          navigate(`/edit-franchise/${franchise._id}`)
                        }
                        className="frc-btn frc-btn--secondary"
                      >
                        Edit
                      </Button>

                      <Popconfirm
                        title={`Mark as ${franchise.status === "active" ? "Inactive" : "Active"}`}
                        description="This will update visibility immediately."
                        onConfirm={() =>
                          handleStatusToggle(franchise._id, franchise.status)
                        }
                        okText="Confirm"
                        cancelText="Cancel"
                        okButtonProps={{ danger: franchise.status === "active" }}
                      >
                        <Button
                          size="small"
                          loading={actionLoadingId === franchise._id}
                          className={`frc-btn ${franchise.status === "active"
                            ? "frc-btn--active"
                            : "frc-btn--inactive"
                            }`}
                        >
                          {franchise.status === "active"
                            ? "Deactivate"
                            : "Activate"}
                        </Button>
                      </Popconfirm>

                      <Link
                        to={`/franchise/${franchise.slug}`}
                        className="frc-card__view-link"
                      >
                        <Button
                          size="small"
                          icon={<EyeOutlined />}
                          className="frc-btn frc-btn--primary"
                        >
                          View
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </section>
        )}

        {/* ── PAGINATION ── */}
        {total > 0 && (
          <div className="frc-pagination">
            <Pagination
              current={page}
              total={total}
              pageSize={15}
              onChange={(p) => setPage(p)}
              showSizeChanger={false}
              responsive
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default MyFranchises;